import * as angular from "angular"
import { createNvzControlService } from "../../nvz/nvz-service"
import { INVZMessages } from "../../nvz/types"
import { FieldViewModel, groupBy, ICropping, sum } from "../../omnia"
import { StockVm } from "../../stocks/stockVm"
import { setupStocks } from "../../stocks/utils"
import { ICroppingBiomass, INutritionBiomassOptions, INutritionPlan, INutritionPlanCropping, INutritionPlanProductApp, INutritionService, IPlanEditArguments, NutrientType, ProductAppInput } from "../../types"
import { CroppingEditVm } from "./cropping/croppingEditVm"
import { ProductApplicationVm } from "./products/productApplicationVm"
import { IPlanEditContext } from "./types"
import { numericTextSort } from "@omnia/shared/utils/numericTextSort"


export class PlanEditContext implements IPlanEditContext {

    public readonly nvz = createNvzControlService(this.svc, () => this.cropping)

    public readonly initialAppTotals = getAppTotals(this.svc, this.initialPlan?.NutritionPlanId)

    public target: INutritionPlan
    public newPlan: boolean;

    private _lastAppEditId: number = 0

    public showCroppingSelector: boolean = true;
    public cropping: CroppingEditVm[];

    public applications: ProductApplicationVm[];
    public stocks: StockVm[];

    public isEditingProduct: boolean;

    private _useBiomass: boolean;
    private _biomassOptions: INutritionBiomassOptions;
    private _biomass: ICroppingBiomass[];

    constructor(
        public readonly svc: INutritionService,
        public readonly args: Readonly<IPlanEditArguments>,
        public readonly readOnly: boolean,
        public readonly initialPlan = args.plan
    ) {

        this.args = args

        this.initialPlan = args.plan
        this.target = this.setupTarget(args.plan)
        this.newPlan = !this.target.NutritionPlanId

        this.setupBiomass();

        this.setupCropping(args.initialCropping);

        this.setupStocks();

        this.setupProducts();

    }

    private setupBiomass() {

        const { target } = this

        const enabled = this._useBiomass = target.Biomass

        const options = this._biomassOptions = target.BiomassOptions || {
            LowRatesForLowValues: false,
            Rounding: 5,
            Auto: true,
        }

        options.MinRate = options.MinRate || {}
        options.MaxRate = options.MaxRate || {}
        options.Auto = !!options.Auto

        if (enabled) {
            this.svc.getBiomassLayers().then(b => this.setupCroppingBiomass(b))
        }

    }

    private setupTarget(plan: INutritionPlan): INutritionPlan {

        if (!plan) {

            return <any>{
                Cropping: [],
                Applications: [],
                MergeAppZones: true,
            }

        }

        var target: INutritionPlan = angular.extend({}, plan);

        target.Cropping = (plan.Cropping || []).map(c => angular.extend({}, c));

        target.Applications = (plan.Applications || []).map(p => {

            var targetProd: INutritionPlanProductApp = angular.extend({}, p);
            return targetProd;

        });

        return target;

    }

    private setupCropping(initialCropping: FieldViewModel[]) {

        this.cropping = CroppingEditVm.createModels(this.svc, this, this.target)

        if (initialCropping) {

            initialCropping
                .filter(c => c.authorized)
                .map(c => this.cropping.find(e => e.croppingId == c.croppingId))
                .filter(c => !!c)
                .forEach(c => c.selected = true)

        }

    }

    private setupCroppingBiomass(biomass: ICroppingBiomass[]) {

        this._biomass = biomass

        this.cropping.forEach(c => {
            c.setupBiomass(biomass.filter(l => l.CroppingId == c.croppingId))
        })
    }

    private setupStocks() {

        this.stocks = setupStocks(this.svc);

    }

    private setupProducts() {

        this.applications = this.target.Applications.map(p => {

            var vm = new ProductApplicationVm(this, this.svc, p);

            vm.stock = this.stocks.find(s => {
                var stockId = s.isFertiliser() ? p.FertiliserId : p.ManureId;
                return (stockId === s.stockId);
            });

            return vm;

        });

    }

    get planId() {
        return this.target.NutritionPlanId
    }

    isValid(): boolean {

        if (!this.hasSelectedCrops()) return false;

        if (!this.applications.every(p => p.isValid())) return false;

        if (this.isEditingProduct) return false;

        return true;

    }

    hasSelectedCrops(): boolean {
        return this.cropping.some(c => c.selected);
    }

    public getSelectedCropping(ignoreRemoved = false): CroppingEditVm[] {

        let cropping = this.cropping.filter(c => c.selected)

        if (ignoreRemoved) {
            cropping = cropping.filter(c => !c.remove)
        }

        return cropping

    }

    get canUseBiomass(): boolean {
        return !this.applications || this.applications.length === 0 || (this.applications.length === 1 && this.applications[0].targetNutrient === NutrientType.N);
    }

    get useBiomass(): boolean {
        return this._useBiomass;
    }

    set useBiomass(value: boolean) {
        this._useBiomass = value;
        this.updatePlanForBiomass(value);
    }

    get biomassOptions(): INutritionBiomassOptions {
        return this._biomassOptions;
    }

    get biomass() {
        return this._biomass
    }

    public updateTargetModel(app: ProductApplicationVm): void {

        this.target = this.getUpdatedTargetModel(false)

        const apps = this.applications || []
        apps.forEach(a => a.recalculateMaxProductRate())

    }

    public getUpdatedTargetModel(api: boolean): INutritionPlan {

        if (!this.cropping || !this.applications) {
            return this.target;
        }

        var target = <INutritionPlan>angular.merge({}, this.target);

        target.HoldingId = this.svc.holdingId;
        target.YearId = this.svc.yearId;
        target.Biomass = this.useBiomass;
        target.BiomassOptions = this.biomassOptions;

        target.Cropping = this.cropping.filter(c => c.selected && (!c.remove)).map(c => {

            var pc: INutritionPlanCropping = <any>{};

            c.updateTargetModel(pc);

            return pc;

        });


        target.Applications = this.applications.filter(p => !p.remove).map(p => {

            const app: INutritionPlanProductApp = <any>angular.extend({}, p.productApp);

            p.updateTargetModel(app, api);

            return app;

        });

        return target;
    }

    public hasOverridesForCropping(croppingId: number): boolean {

        if (this.applications) {
            return this.applications.some(a => a.hasOverrides(croppingId));
        }
    }

    getNVZMessages(croppings: CroppingEditVm[]): INVZMessages {

        if (this.applications) {
            if (this.applications.filter(a => a.analysis && (a.analysis.N > 0)).length === 0) {
                return {
                    error: false,
                    canContinue: true,
                    messages: [],
                    canContinueForClosedPeriod: true
                };
            }
        }

        var closedPeriod = this.checkClosedPeriods(croppings);
        var fieldLimit = this.checkFieldsLimit(croppings);
        var nmax = this.checkNMax(croppings);

        return {
            error: closedPeriod.error || fieldLimit.error || nmax.error,
            canContinue: nmax.canContinue && fieldLimit.canContinue && closedPeriod.canContinue,
            messages: closedPeriod.messages.concat(fieldLimit.messages.concat(nmax.messages)),
            canContinueForClosedPeriod: closedPeriod.error && closedPeriod.canContinue
        };

    }

    checkNMax(cropping: CroppingEditVm[]): INVZMessages {
        let model = this.target;
        return this.nvz.checkNMax(model, cropping);
    }

    checkFieldsLimit(cropping: CroppingEditVm[]): INVZMessages {
        return this.nvz.checkFieldsLimit(this.target, cropping);
    }

    checkClosedPeriods(croppings: CroppingEditVm[]): INVZMessages {
        let model = this.target;
        return this.nvz.checkClosedPeriods(model, croppings);
    }

    getNLeftFieldsLimit(cropping: CroppingEditVm, application: INutritionPlanProductApp): number {
        let model = this.target;
        return this.nvz.getNLeftFieldsLimit(model, cropping, application);
    }

    getParentsSuppliedN(cropping: ICropping, applicationIds: number[], excludedCroppingIds: number[], minDate: Date, maxDate: Date): any {
        return this.nvz.getParentsSuppliedN(this.planId, cropping, applicationIds, excludedCroppingIds, minDate, maxDate);

    }

    get showLime(): boolean {
        if (this.cropping.some(c => c.selected && (c.requirements.RequiredLime > 0))) return true;
        if (this.applications.some(p => p.analysis && (p.analysis.Lime > 0))) return true;
        return false;
    }

    get manure() {
        return this.applications.some(p => p.isManure())
    }


    getCropping(): CroppingEditVm[] {

        let requirements = this.svc.getRequirements()

        let cropping = this.cropping
            .filter(c => !!c.crop)
            .filter(c => requirements.some(r => r.CroppingId === c.croppingId))

        cropping = numericTextSort(cropping, c => c.description)

        return cropping;

    }

    private updatePlanForBiomass(useBiomass: boolean) {

        if (useBiomass && (!this._biomass)) {

            this.svc.getBiomassLayers().then(b => {
                this.setupCroppingBiomass(b)
                this.updatePlanForBiomass(true)
            })

        }

        if (useBiomass && this.applications && this.applications.length) {

            let app = this.applications[0];

            if (app.lastInputUsed == ProductAppInput.NutrientPct) {
                app.lastInputUsed = ProductAppInput.NutrientRate
                app.nutrientPct = null
                app.nutrientRate = 0
            }

        }

    }

    getNextAppEditId() {
        return ++this._lastAppEditId
    }

}

function getAppTotals(svc: INutritionService, planId: number) {

    const applications = svc.getApplications().filter(a => (a.PlanId === planId) && (!a.Zone))

    const groups = groupBy(applications, a => a.AppId)

    const totals = groups.map(g => ({
        appId: g.key,
        amount: sum(g.values, v => v.ProductAmount)
    }))

    return totals

}
